import { Group, Layer, Rect, Stage, Text } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { Fragment, memo, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import styled from "styled-components";

import { Color } from "src/elements";
import {
  moveCoordinatesChecker,
  resizeCoordinatesChecker,
} from "../Draggable/utils/coordinatesChecker";
import type { ResizeSide } from "../../store/types";
import { getOuterBounds, getSnappableFixtures } from "../../store/utils";
import {
  createContainerMoveOperation,
  createContainerResizeOperation,
  createItemPlacementOperation,
  createPegboardPlacementOperation,
  createShelfPlacementOperation,
  createShelfResizeOperation,
  type ContainerBounds,
  type ContainerMoveOperation,
  type ContainerResizeOperation,
  type ItemPlacementOperation,
  type ShelfPlacementOperation,
  type ShelfResizeOperation,
  type PlanogramMoveResult,
} from "./operations";
import {
  areSelectionsEqual,
  clearSelection,
  focusEntity,
  focusProduct,
  getNearestPegboardHole,
  PEGBOARD_HOLE_SNAP_TOLERANCE_PX,
  selectContainer,
  hitTestProduct,
  reconcileSelection,
  selectShelf,
  selectPlacement,
  selectPegboard,
  selectProduct,
} from "./selection";
import { getSharedPlanogramAssetCache } from "./assetCache";
import { createPlanogramSceneProjector } from "./sceneProjection";
import { createViewportModel, type ViewportPoint, type ViewportState } from "./viewport";
import { resolveOverlayPresentation } from "./overlays";
import {
  getOverlayEntities,
  isWorldRectVisible,
  PlacementCells,
  PresentationOverlays,
  ProductCells,
} from "./KonvaPlanogramPresentation";
import type {
  KonvaPlanogramProps,
  PlanogramContextRequest,
  PlanogramEntityRef,
  PlanogramProductAsset,
  PlanogramPolicy,
  PlanogramScene,
  PlanogramSceneContainer,
  PlanogramSceneFixture,
  PlanogramScenePegboard,
  PlanogramScenePlacement,
  PlanogramSceneProduct,
  PlanogramSceneShelf,
  PlanogramSelection,
  PlanogramViewConfig,
} from "./types";
import { emptyPlanogramSelection } from "./types";
import { standardPolicy } from "./standardPolicy";

/** `overflow: visible` so sticky zoom controls bind to the page scroller, not this full-run box. */
const CanvasRoot = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: visible;
  background: ${Color.white};
  touch-action: none;
`;

/** Pinned to the stage corner so 1px SR-only boxes cannot grow the scroll area. */
const AccessibleControls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

const AccessibleProductButton = styled.button`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
`;

const AccessibleProductGroup = styled.div`
  display: contents;
`;

const AccessibleMoveButton = styled.button`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
`;

const AccessibleResizeButton = styled.button`
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
`;

const ViewportControlsLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  pointer-events: none;
`;

/** Sticky to the visible strip — absolute right pinned to the far end of the run. */
const ViewportControls = styled.div`
  position: sticky;
  top: 8px;
  right: 8px;
  margin: 8px;
  display: flex;
  gap: 4px;
  pointer-events: auto;
`;

const ViewportControl = styled.button`
  min-width: 32px;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid ${Color.spaceGray};
  border-radius: 3px;
  background: ${Color.white};
  color: ${Color.textMain};
  cursor: pointer;
`;

const KonvaPlanogramViewportChrome = memo(
  ({
    labels,
    onReset,
    onZoomIn,
    onZoomOut,
  }: {
    labels: KonvaPlanogramProps["labels"];
    onReset: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
  }) => (
    <ViewportControlsLayer data-testid="konva-planogram-viewport-controls-layer">
      <ViewportControls aria-label={labels.viewportControls} role="group">
        <ViewportControl
          aria-label={labels.zoomIn}
          data-testid="konva-planogram-zoom-in"
          onClick={onZoomIn}
          type="button"
        >
          +
        </ViewportControl>
        <ViewportControl
          aria-label={labels.zoomOut}
          data-testid="konva-planogram-zoom-out"
          onClick={onZoomOut}
          type="button"
        >
          −
        </ViewportControl>
        <ViewportControl
          aria-label={labels.resetViewport}
          data-testid="konva-planogram-viewport-reset"
          onClick={onReset}
          type="button"
        >
          {labels.reset}
        </ViewportControl>
      </ViewportControls>
    </ViewportControlsLayer>
  ),
);

const defaultView: Required<
  Pick<PlanogramViewConfig, "offsetX" | "offsetY" | "productDisplayMode" | "productLabel" | "scale">
> = {
  offsetX: 0,
  offsetY: 0,
  productDisplayMode: "label",
  productLabel: "name",
  scale: 1,
};

const GESTURE_THRESHOLD = 8;

const applyStageTranslation = (
  event: KonvaEventObject<MouseEvent | TouchEvent>,
  viewport: ViewportState,
) => {
  const stage = event.target.getStage?.();
  if (!stage || typeof stage.x !== "function" || typeof stage.y !== "function") return;
  stage.x(viewport.offsetX);
  stage.y(viewport.offsetY);
};

const getAssetKey = (product: PlanogramSceneProduct) => `${product.id}:${product.productCode}`;

const combineAccessibleLabels = (label: string, statusLabel: string | null) =>
  statusLabel ? `${label}, ${statusLabel}` : label;

const useStableEvent = <Args extends readonly unknown[], Result>(
  callback: (...args: Args) => Result,
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  return useMemo(
    () =>
      (...args: Args) =>
        callbackRef.current(...args),
    [],
  );
};

interface KonvaEventNode {
  getAttr: (attribute: string) => unknown;
}

interface KonvaEntityEvent {
  readonly currentTarget?: KonvaEventNode;
  readonly target: KonvaEventNode;
}

const getEntityFromEvent = (event: KonvaEntityEvent): PlanogramEntityRef | null => {
  const node = event.currentTarget ?? event.target;
  const id = node.getAttr("id");
  const name = node.getAttr("name");
  if (typeof id !== "string" || typeof name !== "string") return null;

  if (name.startsWith("product-")) return { id, kind: "product" };
  if (name.startsWith("placement-")) return { id, kind: "placement" };
  if (name.startsWith("pegboard-")) return { id, kind: "pegboard" };
  if (name.startsWith("container-")) return { id, kind: "container" };
  if (name.startsWith("shelf-") || name.startsWith("fixture-")) {
    return { id, kind: "shelf" };
  }

  return null;
};

const getStagePointer = (
  event: KonvaEventObject<DragEvent | MouseEvent | PointerEvent | TouchEvent | WheelEvent>,
): ViewportPoint => {
  const stagePointer = event.target.getStage()?.getPointerPosition();
  if (stagePointer) return stagePointer;

  const touchPoint =
    "touches" in event.evt ? (event.evt.touches[0] ?? event.evt.changedTouches[0]) : undefined;

  if (touchPoint) {
    return { x: touchPoint.clientX, y: touchPoint.clientY };
  }

  if ("clientX" in event.evt) {
    return {
      x:
        "offsetX" in event.evt && Number.isFinite(event.evt.offsetX)
          ? event.evt.offsetX
          : event.evt.clientX,
      y:
        "offsetY" in event.evt && Number.isFinite(event.evt.offsetY)
          ? event.evt.offsetY
          : event.evt.clientY,
    };
  }

  return { x: 0, y: 0 };
};

/** At fit zoom the world fills the stage — pan only within the slack past that. */
export const clampViewportOffset = (offset: number, contentSize: number, stageSize: number) => {
  const slack = stageSize - contentSize;
  return Math.min(Math.max(offset, Math.min(0, slack)), Math.max(0, slack));
};

const findScrollPort = (start: HTMLElement | null, axis: "x" | "y") => {
  for (let node: HTMLElement | null = start; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    const overflow = axis === "x" ? style.overflowX : style.overflowY;
    if (overflow !== "auto" && overflow !== "scroll") continue;
    const overflows =
      axis === "x" ? node.scrollWidth > node.clientWidth : node.scrollHeight > node.clientHeight;
    if (overflows) return node;
  }

  return null;
};

const centerStagePointInView = (root: HTMLElement | null, point: ViewportPoint) => {
  if (!root) return;

  const rootRect = root.getBoundingClientRect();
  const horizontal = findScrollPort(root, "x");
  if (horizontal) {
    const portRect = horizontal.getBoundingClientRect();
    horizontal.scrollLeft += rootRect.left - portRect.left + point.x - horizontal.clientWidth / 2;
  }

  const vertical = findScrollPort(root, "y");
  if (vertical) {
    const portRect = vertical.getBoundingClientRect();
    vertical.scrollTop += rootRect.top - portRect.top + point.y - vertical.clientHeight / 2;
  }
};

type AssetRequest = {
  key: string;
  productCode: string;
  productId: string;
};

const useKeyedAssets = (
  requests: readonly AssetRequest[],
  requestKeys: string,
  assetProvider: KonvaPlanogramProps["assetProvider"],
  enabled: boolean,
) => {
  const [assets, setAssets] = useState<Map<string, PlanogramProductAsset>>(() => new Map());
  const [settledKeys, setSettledKeys] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !assetProvider) return;
    const cache = getSharedPlanogramAssetCache(assetProvider);
    let cancelled = false;
    let remaining = requests.length;

    const finishOne = () => {
      remaining -= 1;
      if (remaining === 0) setSettledKeys(requestKeys);
    };

    // Empty lists must settle here; a later effect used to clear this and never finish.
    if (remaining === 0) {
      setSettledKeys(requestKeys);
      return;
    }

    setSettledKeys(null);

    for (const request of requests) {
      void cache
        .load(assetProvider, {
          productCode: request.productCode,
          productId: request.productId,
        })
        .then((asset) => {
          if (cancelled) return;
          if (asset) {
            setAssets((current) => {
              if (current.get(request.key) === asset) return current;
              const next = new Map(current);
              next.set(request.key, asset);
              return next;
            });
          }
          finishOne();
        });
    }

    return () => {
      cancelled = true;
    };
  }, [assetProvider, enabled, requestKeys]);

  useEffect(() => {
    const liveKeys = new Set(requests.map((request) => request.key));
    setAssets((current) => {
      const next = new Map([...current].filter(([key]) => liveKeys.has(key)));
      return next.size === current.size ? current : next;
    });
  }, [requestKeys]);

  useEffect(() => {
    setAssets(new Map());
  }, [assetProvider]);

  const settled = !enabled || !assetProvider || settledKeys === requestKeys;

  return { assets, settled };
};

const useProductAssets = (
  products: PlanogramSceneProduct[],
  assetProvider: KonvaPlanogramProps["assetProvider"],
  enabled: boolean,
) =>
  useKeyedAssets(
    products.map((product) => ({
      key: getAssetKey(product),
      productCode: product.productCode,
      productId: product.id,
    })),
    products.map(getAssetKey).join("|"),
    assetProvider,
    enabled,
  );

const usePlacementAssets = (
  placements: readonly PlanogramScenePlacement[],
  assetProvider: KonvaPlanogramProps["assetProvider"],
  enabled: boolean,
) =>
  useKeyedAssets(
    placements.map((placement) => ({
      key: placement.id,
      productCode: placement.productCode,
      productId: placement.productId,
    })),
    placements
      .map((placement) => `${placement.id}:${placement.productId}:${placement.productCode}`)
      .join("|"),
    assetProvider,
    enabled,
  );

const isSelected = (selection: PlanogramSelection, productId: string) =>
  selection.kind === "product" && selection.ids.includes(productId);

const getProductEntity = (product: PlanogramSceneProduct): PlanogramEntityRef => ({
  id: product.id,
  kind: "product",
});

const getPlacementEntity = (placement: PlanogramScenePlacement): PlanogramEntityRef => ({
  id: placement.id,
  kind: "placement",
});

const getPegboardEntity = (pegboard: PlanogramScenePegboard): PlanogramEntityRef => ({
  id: pegboard.id,
  kind: "pegboard",
});

const getContainerEntity = (container: PlanogramSceneContainer): PlanogramEntityRef => ({
  id: container.id,
  kind: "container",
});

interface ActiveShelfDrag {
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  readonly shelfId: string;
  readonly shelfIds: readonly string[];
  readonly startWorldY: number;
  completed: boolean;
}

interface ActivePlacementDrag {
  readonly activePlacementId: string;
  readonly placementIds: readonly string[];
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  completed: boolean;
}

interface ActiveContainerDrag {
  readonly containerId: string;
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  readonly startWorld: ViewportPoint;
  completed: boolean;
}

interface ActiveContainerResize {
  readonly containerId: string;
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  readonly side: ResizeSide;
  completed: boolean;
}

const CONTAINER_RESIZE_SIDES: readonly ResizeSide[] = [
  "top-left",
  "top",
  "top-right",
  "right",
  "bottom-right",
  "bottom",
  "bottom-left",
  "left",
];

const getContainerResizeHandlePosition = (
  container: PlanogramSceneContainer,
  side: ResizeSide,
  handleSize: number,
): ViewportPoint => {
  const left = container.x;
  const centerX = container.x + container.width / 2 - handleSize / 2;
  const right = container.x + container.width - handleSize;
  const top = container.y;
  const middleY = container.y + container.height / 2 - handleSize / 2;
  const bottom = container.y + container.height - handleSize;

  switch (side) {
    case "top-left":
      return { x: left, y: top };
    case "top":
      return { x: centerX, y: top };
    case "top-right":
      return { x: right, y: top };
    case "right":
      return { x: right, y: middleY };
    case "bottom-right":
      return { x: right, y: bottom };
    case "bottom":
      return { x: centerX, y: bottom };
    case "bottom-left":
      return { x: left, y: bottom };
    case "left":
      return { x: left, y: middleY };
  }
};

const resizesLeft = (side: ResizeSide) =>
  side === "left" || side === "top-left" || side === "bottom-left";
const resizesRight = (side: ResizeSide) =>
  side === "right" || side === "top-right" || side === "bottom-right";
const resizesTop = (side: ResizeSide) =>
  side === "top" || side === "top-left" || side === "top-right";
const resizesBottom = (side: ResizeSide) =>
  side === "bottom" || side === "bottom-left" || side === "bottom-right";

interface ActiveProductDrag {
  readonly activeItemId: string;
  readonly cancelVisual: () => void;
  readonly intent: "copy" | "move";
  readonly itemIds: readonly string[];
  readonly restoreVisual: () => void;
  readonly selection: PlanogramSelection;
  completed: boolean;
}

interface ProductDragPreview {
  readonly activeItemId: string;
  readonly itemIds: readonly string[];
}

const getInsertionSide = (product: PlanogramSceneProduct, worldPoint: ViewportPoint) =>
  worldPoint.x < product.x + product.width / 2 ? "left" : "right";

interface ProductDropHint {
  readonly caretX: number;
  readonly width: number;
  readonly y: number;
  readonly height: number;
  readonly targetId: string;
}

interface DragSimulation {
  readonly key: string;
  readonly scene: PlanogramScene;
  readonly hint: ProductDropHint | null;
}

type RelativeMoveDirection = "left" | "right";
type RelativeMoveTargets = ReadonlyMap<
  string,
  Partial<Record<RelativeMoveDirection, PlanogramSceneProduct>>
>;

export const createRelativeMoveTargets = (
  products: readonly PlanogramSceneProduct[],
  selectedProductIds: Pick<ReadonlySet<string>, "has">,
): RelativeMoveTargets => {
  const groups = new Map<string, PlanogramSceneProduct[]>();
  const targets = new Map<string, Partial<Record<RelativeMoveDirection, PlanogramSceneProduct>>>();

  for (const product of products) {
    const group = groups.get(product.shelfGroupId) ?? [];
    group.push(product);
    groups.set(product.shelfGroupId, group);
  }

  for (const group of groups.values()) {
    const previousUnselected: Array<PlanogramSceneProduct | undefined> = [];
    const nextUnselected: Array<PlanogramSceneProduct | undefined> = [];
    let nearest: PlanogramSceneProduct | undefined;
    for (let index = 0; index < group.length; index += 1) {
      const product = group[index];
      previousUnselected[index] = nearest;
      if (product && !selectedProductIds.has(product.id)) nearest = product;
    }

    nearest = undefined;
    for (let index = group.length - 1; index >= 0; index -= 1) {
      const product = group[index];
      nextUnselected[index] = nearest;
      if (product && !selectedProductIds.has(product.id)) nearest = product;
    }

    for (let index = 0; index < group.length; index += 1) {
      const product = group[index];
      if (!product) continue;
      const isSelected = selectedProductIds.has(product.id);
      targets.set(product.id, {
        left: isSelected ? previousUnselected[index] : group[index - 1],
        right: isSelected ? nextUnselected[index] : group[index + 1],
      });
    }
  }

  return targets;
};

const getDragSelection = (
  scene: Parameters<typeof selectProduct>[0],
  selection: PlanogramSelection,
  productId: string,
) => (isSelected(selection, productId) ? selection : selectProduct(scene, selection, productId));

export const KonvaPlanogram = ({
  assetProvider,
  dropPreview,
  labels,
  onActivate,
  onAssetsSettled,
  onContextRequest,
  onOverlayBadgeActivate,
  overlayBadges,
  overlayChannels,
  onCommit,
  onHoverChange,
  onMoveResult,
  onSelectionChange,
  onViewportChange,
  policy = standardPolicy,
  pog,
  scrollToEntityId,
  selection = emptyPlanogramSelection,
  stageRef,
  viewport,
  viewportConfig,
  view,
}: KonvaPlanogramProps) => {
  const resolvedView = { ...defaultView, ...view };
  const viewportModel = useMemo(
    () =>
      createViewportModel({
        ...viewportConfig,
        defaultState: {
          offsetX: resolvedView.offsetX,
          offsetY: resolvedView.offsetY,
          scale: resolvedView.scale,
          ...viewportConfig?.defaultState,
        },
      }),
    [
      resolvedView.offsetX,
      resolvedView.offsetY,
      resolvedView.scale,
      viewportConfig?.bounds?.maxScale,
      viewportConfig?.bounds?.minScale,
      viewportConfig?.defaultState?.offsetX,
      viewportConfig?.defaultState?.offsetY,
      viewportConfig?.defaultState?.scale,
    ],
  );
  const [uncontrolledViewport, setUncontrolledViewport] = useState<ViewportState>(
    () => viewportModel.defaultState,
  );
  const previousDefaultScale = useRef(viewportModel.defaultState.scale);
  useEffect(() => {
    if (viewport) return;

    const previousScale = previousDefaultScale.current;
    previousDefaultScale.current = viewportModel.defaultState.scale;
    setUncontrolledViewport((current) =>
      viewportModel.createState({
        ...current,
        scale: current.scale * (viewportModel.defaultState.scale / previousScale),
      }),
    );
  }, [viewport, viewportModel]);
  const activeViewport = viewport ? viewportModel.createState(viewport) : uncontrolledViewport;
  const liveViewportRef = useRef(activeViewport);
  const panPointerRef = useRef<ViewportPoint | null>(null);
  if (!panPointerRef.current) {
    liveViewportRef.current = activeViewport;
  }
  const panOriginRef = useRef<ViewportPoint | null>(null);
  const backgroundPanMovedRef = useRef(false);
  const activeProductDragRef = useRef<ActiveProductDrag | null>(null);
  const activePlacementDragRef = useRef<ActivePlacementDrag | null>(null);
  const activeShelfDragRef = useRef(new Map<string, ActiveShelfDrag>());
  const activeContainerDragRef = useRef<ActiveContainerDrag | null>(null);
  const activeContainerResizeRef = useRef<ActiveContainerResize | null>(null);
  const [productDragPreview, setProductDragPreview] = useState<ProductDragPreview | null>(null);
  const [dropHint, setDropHint] = useState<ProductDropHint | null>(null);
  const [dragSimulation, setDragSimulation] = useState<DragSimulation | null>(null);
  const setDragSimulationIfChanged = (next: DragSimulation | null) => {
    setDragSimulation((current) => {
      if (current === next) return current;
      if (current && next && current.key === next.key) return current;
      return next;
    });
  };
  /** Pointer moves fire continuously; only a changed seam should re-render. */
  const setDropHintIfChanged = (next: ProductDropHint | null) => {
    setDropHint((current) => {
      if (current === next) return current;
      if (
        current &&
        next &&
        current.caretX === next.caretX &&
        current.width === next.width &&
        current.y === next.y &&
        current.height === next.height &&
        current.targetId === next.targetId
      ) {
        return current;
      }
      return next;
    });
  };
  const [hoveredEntity, setHoveredEntityState] = useState<PlanogramEntityRef | null>(null);
  const hoveredId = hoveredEntity?.id ?? null;
  const canvasRootRef = useRef<HTMLDivElement>(null);
  const productButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const pegboardButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const placementButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const [sceneProjector] = useState(createPlanogramSceneProjector);
  const scene = useMemo(
    () => sceneProjector.project(pog, resolvedView.productLabel),
    [pog, resolvedView.productLabel, sceneProjector],
  );
  const renderedProducts = useMemo(() => {
    if (!dragSimulation) return scene.products;
    const inHand = new Set(productDragPreview?.itemIds ?? []);
    const simulated = new Map(dragSimulation.scene.products.map((item) => [item.id, item]));
    return scene.products.map((product) =>
      inHand.has(product.id) ? product : (simulated.get(product.id) ?? product),
    );
  }, [dragSimulation, productDragPreview, scene.products]);
  const projectionStats = sceneProjector.getStats();
  const scenePegboards = scene.pegboards ?? [];
  const scenePlacements = scene.placements ?? [];
  const activeSelection = useMemo(() => reconcileSelection(selection, scene), [scene, selection]);
  const selectedProductIds = useMemo(
    () => (activeSelection.kind === "product" ? new Set(activeSelection.ids) : new Set<string>()),
    [activeSelection],
  );
  const relativeMoveTargets = useMemo(
    () => createRelativeMoveTargets(scene.products, selectedProductIds),
    [scene.products, selectedProductIds],
  );
  const overlayEntities = useMemo(() => getOverlayEntities(scene), [scene]);
  const worldWidth = resolvedView.width ?? scene.width;
  const worldHeight = resolvedView.height ?? scene.height;
  // Clamp to the browser canvas AREA cap (backing px = CSS × DPR²). Past it the
  // canvas paints nothing, silently, with a full scene graph still mounted.
  const MAX_CANVAS_SIDE_PX = 32767;
  const MAX_CANVAS_AREA_PX = 268435456;
  const dpr = typeof window === "undefined" ? 1 : Math.max(1, window.devicePixelRatio || 1);
  const stageHeight = Math.min(
    MAX_CANVAS_SIDE_PX / dpr,
    Math.max(1, worldHeight * viewportModel.defaultState.scale, worldHeight * activeViewport.scale),
  );
  const maxWidthByArea = MAX_CANVAS_AREA_PX / (dpr * dpr * Math.max(1, stageHeight));
  const stageWidth = Math.min(
    MAX_CANVAS_SIDE_PX / dpr,
    maxWidthByArea,
    Math.max(1, worldWidth * viewportModel.defaultState.scale),
  );
  const showProductLabels =
    resolvedView.productDisplayMode === "label" && resolvedView.productLabel !== "none";
  const globalLabelFontSize = useMemo(() => {
    return Math.max(16, (worldHeight / 600) * 12);
  }, [worldHeight]);
  const isProductVisible = (product: PlanogramSceneProduct) =>
    isWorldRectVisible(product, activeViewport, stageWidth, stageHeight);
  const overlayPresentations = useMemo(
    () =>
      resolveOverlayPresentation({
        badges: overlayBadges,
        channels: overlayChannels,
        entityIds: overlayEntities.map((entity) => entity.id),
        hoveredId,
        selection: {
          focusedId: activeSelection.focusedId,
          ids: activeSelection.ids,
        },
      }),
    [
      activeSelection.focusedId,
      activeSelection.ids,
      hoveredId,
      overlayBadges,
      overlayChannels,
      overlayEntities,
    ],
  );
  const { assets, settled: productAssetsSettled } = useProductAssets(
    scene.products,
    assetProvider,
    resolvedView.productDisplayMode === "image",
  );
  const { assets: placementAssets, settled: placementAssetsSettled } = usePlacementAssets(
    scenePlacements,
    assetProvider,
    resolvedView.productDisplayMode === "image",
  );
  const assetsSettled = productAssetsSettled && placementAssetsSettled;
  const emitAssetsSettled = useStableEvent(() => onAssetsSettled?.());
  useEffect(() => {
    if (assetsSettled) emitAssetsSettled();
  }, [assetsSettled, emitAssetsSettled, scene]);
  const updateViewport = (nextViewport: ViewportState) => {
    const nextState = viewportModel.createState(nextViewport);
    if (!viewport) setUncontrolledViewport(nextState);
    onViewportChange?.(nextState);
  };
  const zoomIn = useStableEvent(() => {
    updateViewport(
      viewportModel.zoomBy(liveViewportRef.current, 1.1, {
        x: stageWidth / 2,
        y: stageHeight / 2,
      }),
    );
  });
  const zoomOut = useStableEvent(() => {
    updateViewport(
      viewportModel.zoomBy(liveViewportRef.current, 1 / 1.1, {
        x: stageWidth / 2,
        y: stageHeight / 2,
      }),
    );
  });
  const resetViewport = useStableEvent(() => updateViewport(viewportModel.reset()));

  useEffect(() => {
    if (!areSelectionsEqual(selection, activeSelection)) {
      onSelectionChange?.(activeSelection);
    }
  }, [activeSelection, onSelectionChange, selection]);

  const updateSelection = (nextSelection: PlanogramSelection) => {
    if (!areSelectionsEqual(activeSelection, nextSelection)) {
      onSelectionChange?.(nextSelection);
    }
  };

  const setHoveredEntity = (entity: PlanogramEntityRef) => setHoveredEntityState(entity);
  const clearHoveredEntity = (id: string) =>
    setHoveredEntityState((current) => (current?.id === id ? null : current));

  const emitHoverChange = useStableEvent((entity: PlanogramEntityRef | null) =>
    onHoverChange?.(entity),
  );
  useEffect(() => {
    emitHoverChange(hoveredEntity);
  }, [emitHoverChange, hoveredEntity]);
  useEffect(() => () => emitHoverChange(null), [emitHoverChange]);

  // Pan only within stage slack (none at fit zoom); scroll the ancestor strip instead.
  const lastScrolledEntityRef = useRef<string | null>(null);
  useEffect(() => {
    const nextEntityId = scrollToEntityId ?? null;
    if (nextEntityId === lastScrolledEntityRef.current) return;
    lastScrolledEntityRef.current = nextEntityId;
    if (!nextEntityId) return;

    const entity =
      scene.products.find((candidate) => candidate.id === nextEntityId) ??
      scenePlacements.find((candidate) => candidate.id === nextEntityId) ??
      scene.containers.find((candidate) => candidate.id === nextEntityId);
    if (!entity) return;

    const center = { x: entity.x + entity.width / 2, y: entity.y + entity.height / 2 };
    const nextViewport = viewportModel.panTo(activeViewport, {
      x: clampViewportOffset(
        stageWidth / 2 - center.x * activeViewport.scale,
        worldWidth * activeViewport.scale,
        stageWidth,
      ),
      y: clampViewportOffset(
        stageHeight / 2 - center.y * activeViewport.scale,
        worldHeight * activeViewport.scale,
        stageHeight,
      ),
    });

    updateViewport(nextViewport);
    centerStagePointInView(canvasRootRef.current, viewportModel.worldToStage(nextViewport, center));
  });

  const startBackgroundPan = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (event.target !== event.target.getStage()) return;
    backgroundPanMovedRef.current = false;
    liveViewportRef.current = activeViewport;
    const pointer = getStagePointer(event);
    panOriginRef.current = pointer;
    panPointerRef.current = pointer;
  };

  const continueBackgroundPan = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    const previousPointer = panPointerRef.current;
    const origin = panOriginRef.current;
    if (!previousPointer || !origin) return;

    const nextPointer = getStagePointer(event);
    if (!backgroundPanMovedRef.current) {
      const distance = Math.hypot(nextPointer.x - origin.x, nextPointer.y - origin.y);
      if (distance < GESTURE_THRESHOLD) {
        panPointerRef.current = nextPointer;
        return;
      }

      backgroundPanMovedRef.current = true;
      panPointerRef.current = origin;
    }

    const panPointer = panPointerRef.current;
    if (!panPointer) return;

    const panned = viewportModel.panBy(liveViewportRef.current, {
      x: nextPointer.x - panPointer.x,
      y: nextPointer.y - panPointer.y,
    });
    const nextViewport = viewportModel.createState({
      ...panned,
      offsetX: clampViewportOffset(panned.offsetX, worldWidth * panned.scale, stageWidth),
      offsetY: clampViewportOffset(panned.offsetY, worldHeight * panned.scale, stageHeight),
    });
    // Pointer moves must not write React every frame — apply the stage transform here.
    liveViewportRef.current = nextViewport;
    applyStageTranslation(event, nextViewport);
    panPointerRef.current = nextPointer;
  };

  const commitBackgroundPan = () => {
    if (!backgroundPanMovedRef.current) return;
    updateViewport(liveViewportRef.current);
  };

  const stopBackgroundPan = () => {
    commitBackgroundPan();
    panPointerRef.current = null;
    panOriginRef.current = null;
  };

  const cancelBackgroundPan = () => {
    commitBackgroundPan();
    panPointerRef.current = null;
    panOriginRef.current = null;
    backgroundPanMovedRef.current = false;
  };

  const cancelProductDrag = () => {
    const activeDrag = activeProductDragRef.current;
    if (!activeDrag || activeDrag.completed) return;

    activeDrag.completed = true;
    activeDrag.cancelVisual();
    activeProductDragRef.current = null;
    setProductDragPreview(null);
    setDropHintIfChanged(null);
    setDragSimulationIfChanged(null);
  };

  const cancelShelfDrag = () => {
    for (const activeDrag of activeShelfDragRef.current.values()) {
      if (activeDrag.completed) continue;
      activeDrag.completed = true;
      activeDrag.restoreVisual();
    }
    activeShelfDragRef.current.clear();
  };

  const cancelPlacementDrag = () => {
    const activeDrag = activePlacementDragRef.current;
    if (!activeDrag || activeDrag.completed) return;
    activeDrag.completed = true;
    activeDrag.restoreVisual();
    activePlacementDragRef.current = null;
  };

  const cancelContainerInteractions = () => {
    const activeDrag = activeContainerDragRef.current;
    if (activeDrag && !activeDrag.completed) {
      activeDrag.completed = true;
      activeDrag.restoreVisual();
    }
    activeContainerDragRef.current = null;

    const activeResize = activeContainerResizeRef.current;
    if (activeResize && !activeResize.completed) {
      activeResize.completed = true;
      activeResize.restoreVisual();
    }
    activeContainerResizeRef.current = null;
  };

  const cancelInteractions = () => {
    cancelBackgroundPan();
    cancelProductDrag();
    cancelShelfDrag();
    cancelPlacementDrag();
    cancelContainerInteractions();
  };

  const cancelOnEscape = useStableEvent((event: globalThis.KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    cancelInteractions();
  });
  const dragInProgress = productDragPreview !== null;
  useEffect(() => {
    if (!dragInProgress) return;
    window.addEventListener("keydown", cancelOnEscape);
    return () => window.removeEventListener("keydown", cancelOnEscape);
  }, [cancelOnEscape, dragInProgress]);

  const handleWheel = (event: KonvaEventObject<WheelEvent>) => {
    event.evt.preventDefault();
    const zoomFactor = event.evt.deltaY < 0 ? 1.1 : 1 / 1.1;
    updateViewport(viewportModel.zoomBy(activeViewport, zoomFactor, getStagePointer(event)));
  };

  const canMove = policy.canMove ?? true;

  const canSelectShelf = (shelfId: string) =>
    policy.canSelect?.({ id: shelfId, kind: "shelf" }) ?? true;

  const canSelectContainer = (containerId: string) =>
    policy.canSelect?.({ id: containerId, kind: "container" }) ?? true;

  const handleShelfSelection = (
    shelfId: string,
    event: { ctrlKey?: boolean; metaKey?: boolean },
  ) => {
    if (!canSelectShelf(shelfId)) return;
    updateSelection(selectShelf(scene, activeSelection, shelfId, event));
  };

  const submitShelfOperation = (
    operation: ShelfPlacementOperation | ShelfResizeOperation,
    shelfSelection: PlanogramSelection,
    restoreVisual: () => void,
  ) => {
    const result = policy.execute({ operation, pog, selection: shelfSelection });
    emitMoveResult(operation, result);
    if (result.status !== "committed") {
      restoreVisual();
      return;
    }
    updateSelection(result.selection);
    onCommit?.({ operation, pog: result.pog, selection: result.selection });
  };

  const handleShelfDragStart = (shelfId: string, event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    if (!canSelectShelf(shelfId)) {
      event.target.stopDrag();
      return;
    }
    const shelfSelection =
      activeSelection.kind === "shelf" && activeSelection.ids.includes(shelfId)
        ? activeSelection
        : selectShelf(scene, activeSelection, shelfId);
    const domainShelf = pog.getShelf(shelfId);
    if (!domainShelf) {
      event.target.stopDrag();
      return;
    }
    updateSelection(shelfSelection);
    const startPointer = getStagePointer(event);
    const originalPosition = event.target.position();
    activeShelfDragRef.current.set(shelfId, {
      completed: false,
      restoreVisual: () => event.target.position(originalPosition),
      selection: shelfSelection,
      shelfId,
      shelfIds: shelfSelection.ids,
      startWorldY: viewportModel.stageToWorld(activeViewport, startPointer).y,
    });
  };

  const handleShelfDragEnd = (
    shelf: PlanogramSceneShelf | PlanogramSceneFixture,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const drag = activeShelfDragRef.current.get(shelf.id);
    if (!drag || drag.completed || drag.shelfId !== shelf.id) return;
    drag.completed = true;
    activeShelfDragRef.current.delete(shelf.id);
    const domainShelf = pog.getShelf(shelf.id);
    if (!domainShelf) {
      drag.restoreVisual();
      return;
    }
    const pointer = getStagePointer(event);
    const pointerWorldY = viewportModel.stageToWorld(activeViewport, pointer).y;
    const deltaNotches = Math.round(
      (drag.startWorldY - pointerWorldY) / Math.max(domainShelf.bay.notchSpacing, 1),
    );
    const operation = createShelfPlacementOperation({
      activeShelfId: shelf.id,
      bayId: domainShelf.bay.uniqueId,
      notchNo: domainShelf.getNotchNo() + deltaNotches,
      shelfIds: drag.shelfIds,
    });
    submitShelfOperation(operation, drag.selection, drag.restoreVisual);
  };

  const getContainerSnappables = (containerId: string) => [
    ...getSnappableFixtures(pog.planogram),
    ...pog.containers
      .filter((candidate) => candidate.uniqueId !== containerId)
      .map((candidate) => ({
        xLeft: candidate.xLeft,
        xRight: candidate.xRight,
        yBottom: candidate.yBottom,
        yTop: candidate.yTop,
      })),
  ];

  const getContainerDragSelection = (containerId: string) =>
    activeSelection.kind === "container" && activeSelection.ids.includes(containerId)
      ? activeSelection
      : selectContainer(scene, activeSelection, containerId);

  const submitContainerOperation = (
    operation: ContainerMoveOperation | ContainerResizeOperation,
    containerSelection: PlanogramSelection,
    restoreVisual: () => void,
  ) => {
    const result = policy.execute({ operation, pog, selection: containerSelection });
    emitMoveResult(operation, result);
    restoreVisual();
    if (result.status !== "committed") return;
    updateSelection(result.selection);
    onCommit?.({ operation, pog: result.pog, selection: result.selection });
  };

  const handleContainerDragStart = (containerId: string, event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;
    if (!canSelectContainer(containerId)) {
      event.target.stopDrag();
      return;
    }
    const containerSelection = getContainerDragSelection(containerId);
    updateSelection(containerSelection);
    const dragNode = event.target;
    const originalPosition = dragNode.position();
    activeContainerDragRef.current = {
      completed: false,
      containerId,
      restoreVisual: () => dragNode.position(originalPosition),
      selection: containerSelection,
      startWorld: viewportModel.stageToWorld(activeViewport, getStagePointer(event)),
    };
  };

  const handleContainerDragEnd = (
    container: PlanogramSceneContainer,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const drag = activeContainerDragRef.current;
    if (!drag || drag.completed || drag.containerId !== container.id) return;
    drag.completed = true;
    activeContainerDragRef.current = null;
    const domainContainer = pog.containers.find((candidate) => candidate.uniqueId === container.id);
    if (!domainContainer) {
      drag.restoreVisual();
      return;
    }
    const endWorld = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const deltaX = endWorld.x - drag.startWorld.x;
    const deltaDomainY = drag.startWorld.y - endWorld.y;
    const bounds: ContainerBounds = moveCoordinatesChecker({
      bounds: getOuterBounds(pog.planogram),
      scaleX: activeViewport.scale,
      scaleY: activeViewport.scale,
      snappables: getContainerSnappables(container.id),
      xLeft: domainContainer.xLeft + deltaX,
      xRight: domainContainer.xRight + deltaX,
      yBottom: domainContainer.yBottom + deltaDomainY,
      yTop: domainContainer.yTop + deltaDomainY,
    });
    submitContainerOperation(
      createContainerMoveOperation({ bounds, containerId: container.id }),
      drag.selection,
      drag.restoreVisual,
    );
  };

  const handleContainerResizeStart = (
    containerId: string,
    side: ResizeSide,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    if (!canSelectContainer(containerId)) {
      event.target.stopDrag();
      return;
    }
    const containerSelection = getContainerDragSelection(containerId);
    updateSelection(containerSelection);
    const dragNode = event.target;
    const originalPosition = dragNode.position();
    activeContainerResizeRef.current = {
      completed: false,
      containerId,
      restoreVisual: () => dragNode.position(originalPosition),
      selection: containerSelection,
      side,
    };
  };

  const handleContainerResizeEnd = (
    container: PlanogramSceneContainer,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const resize = activeContainerResizeRef.current;
    if (!resize || resize.completed || resize.containerId !== container.id) return;
    resize.completed = true;
    activeContainerResizeRef.current = null;
    const domainContainer = pog.containers.find((candidate) => candidate.uniqueId === container.id);
    if (!domainContainer) {
      resize.restoreVisual();
      return;
    }
    const endWorld = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const pointerDomainX = endWorld.x;
    const pointerDomainY = scene.height - endWorld.y;
    const bounds: ContainerBounds = resizeCoordinatesChecker({
      bounds: getOuterBounds(pog.planogram),
      scaleX: activeViewport.scale,
      scaleY: activeViewport.scale,
      side: resize.side,
      snappables: getContainerSnappables(container.id),
      xLeft: resizesLeft(resize.side) ? pointerDomainX : domainContainer.xLeft,
      xRight: resizesRight(resize.side) ? pointerDomainX : domainContainer.xRight,
      yBottom: resizesBottom(resize.side) ? pointerDomainY : domainContainer.yBottom,
      yTop: resizesTop(resize.side) ? pointerDomainY : domainContainer.yTop,
    });
    submitContainerOperation(
      createContainerResizeOperation({ bounds, containerId: container.id }),
      resize.selection,
      resize.restoreVisual,
    );
  };

  const canSelectPegboard = (pegboardId: string) =>
    policy.canSelect?.({ id: pegboardId, kind: "pegboard" }) ?? true;

  const canSelectPlacement = (placementId: string) =>
    policy.canSelect?.({ id: placementId, kind: "placement" }) ?? true;

  const canSelectProduct = (product: PlanogramSceneProduct) => {
    const entity = getProductEntity(product);
    return policy.canSelect?.(entity) ?? true;
  };

  const handleSelection = (
    product: PlanogramSceneProduct,
    event: { ctrlKey?: boolean; metaKey?: boolean },
  ) => {
    if (!canSelectProduct(product)) return;
    updateSelection(selectProduct(scene, activeSelection, product.id, event));
  };

  const activateContainer = (containerId: string) => {
    if (!canSelectContainer(containerId)) return;
    const container = scene.containers.find((candidate) => candidate.id === containerId);
    if (!container) return;
    const nextSelection = selectContainer(scene, activeSelection, containerId);
    updateSelection(nextSelection);
    onActivate?.(getContainerEntity(container), nextSelection);
  };

  const activatePegboard = (pegboardId: string) => {
    if (!canSelectPegboard(pegboardId)) return;
    const pegboard = scenePegboards.find((candidate) => candidate.id === pegboardId);
    if (!pegboard) return;
    const nextSelection = selectPegboard(scene, activeSelection, pegboardId);
    updateSelection(nextSelection);
    onActivate?.(getPegboardEntity(pegboard), nextSelection);
  };

  const activatePlacement = (placement: PlanogramScenePlacement) => {
    if (!canSelectPlacement(placement.id)) return;
    const nextSelection = selectPlacement(scene, activeSelection, placement.id);
    updateSelection(nextSelection);
    onActivate?.(getPlacementEntity(placement), nextSelection);
  };

  const activateProduct = (product: PlanogramSceneProduct) => {
    if (!canSelectProduct(product)) return;
    const nextSelection = selectProduct(scene, activeSelection, product.id);
    updateSelection(nextSelection);
    onActivate?.(getProductEntity(product), nextSelection);
  };

  const activateShelf = (shelfId: string) => {
    if (!canSelectShelf(shelfId)) return;
    const nextSelection = selectShelf(scene, activeSelection, shelfId);
    updateSelection(nextSelection);
    onActivate?.({ id: shelfId, kind: "shelf" }, nextSelection);
  };

  const getRelativeMove = (
    product: PlanogramSceneProduct,
    direction: RelativeMoveDirection,
  ): {
    capability: ReturnType<PlanogramPolicy["evaluate"]>;
    operation: ItemPlacementOperation;
    selection: PlanogramSelection;
  } | null => {
    if (!canSelectProduct(product)) return null;

    const moveSelection: PlanogramSelection = selectedProductIds.has(product.id)
      ? activeSelection
      : {
          anchorId: product.id,
          focusedId: product.id,
          ids: [product.id],
          kind: "product",
        };
    const targetProduct = relativeMoveTargets.get(product.id)?.[direction];
    if (!targetProduct) return null;

    const operation = createItemPlacementOperation({
      activeItemId: product.id,
      intent: "move",
      side: direction,
      sourceItemIds: moveSelection.ids,
      targetItemId: targetProduct.id,
    });

    const capability = policy.evaluate(pog, operation);
    if (!capability.allowed) return null;

    return {
      capability,
      operation,
      selection: moveSelection,
    };
  };

  const handleFocus = (product: PlanogramSceneProduct) => {
    updateSelection(focusProduct(scene, activeSelection, product.id));
  };

  const handleBackgroundSelection = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (event.target !== event.target.getStage()) return;

    if (!backgroundPanMovedRef.current) {
      updateSelection(clearSelection());
    }
    backgroundPanMovedRef.current = false;
  };

  const handlePlacementDragStart = (
    placement: PlanogramScenePlacement,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    if (!canSelectPlacement(placement.id)) {
      event.target.stopDrag();
      return;
    }
    const dragSelection =
      activeSelection.kind === "placement" && activeSelection.ids.includes(placement.id)
        ? activeSelection
        : selectPlacement(scene, activeSelection, placement.id);
    const dragNode = event.target;
    activePlacementDragRef.current = {
      activePlacementId: placement.id,
      completed: false,
      placementIds: dragSelection.ids,
      restoreVisual: () => dragNode.position({ x: placement.x, y: placement.y }),
      selection: dragSelection,
    };
    updateSelection(dragSelection);
  };

  const handlePlacementDragEnd = (
    placement: PlanogramScenePlacement,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const activeDrag = activePlacementDragRef.current;
    if (!activeDrag || activeDrag.completed || activeDrag.activePlacementId !== placement.id)
      return;
    activeDrag.completed = true;
    activePlacementDragRef.current = null;
    activeDrag.restoreVisual();
    const target = getNearestPegboardHole(
      scene,
      viewportModel.stageToWorld(activeViewport, getStagePointer(event)),
      PEGBOARD_HOLE_SNAP_TOLERANCE_PX / activeViewport.scale,
    );
    if (!target) {
      emitMoveResult(null, {
        pog,
        reason: "missing-target",
        selection: activeDrag.selection,
        status: "rejected",
      });
      return;
    }
    const operation = createPegboardPlacementOperation({
      activePlacementId: placement.id,
      holeNoLeft: target.holeNoLeft,
      holeNoTop: target.holeNoTop,
      pegboardId: target.pegboardId,
      placementIds: activeDrag.placementIds,
    });
    const result = policy.execute({ operation, pog, selection: activeDrag.selection });
    emitMoveResult(operation, result);
    if (result.status !== "committed") return;
    updateSelection(result.selection);
    onCommit?.({ operation, pog: result.pog, selection: result.selection });
  };

  const handleProductDragStart = (
    product: PlanogramSceneProduct,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    if (!canSelectProduct(product)) {
      event.target.stopDrag();
      return;
    }

    const dragSelection = getDragSelection(scene, activeSelection, product.id);
    const selectedIds = new Set(dragSelection.ids);
    const itemIds = scene.products
      .filter((candidate) => selectedIds.has(candidate.id))
      .map((candidate) => candidate.id);
    const dragNode = event.target;
    activeProductDragRef.current = {
      activeItemId: product.id,
      cancelVisual: () => {
        dragNode.stopDrag();
        dragNode.position({ x: product.x, y: product.y });
      },
      completed: false,
      intent: event.evt?.ctrlKey || event.evt?.metaKey ? "copy" : "move",
      itemIds,
      restoreVisual: () => dragNode.position({ x: product.x, y: product.y }),
      selection: dragSelection,
    };
    setProductDragPreview({ activeItemId: product.id, itemIds });

    updateSelection(dragSelection);
  };

  const emitMoveResult = (
    operation: Parameters<NonNullable<KonvaPlanogramProps["onMoveResult"]>>[0]["operation"],
    result: PlanogramMoveResult,
  ) => {
    onMoveResult?.({ operation, result });
  };

  const submitMoveOperation = (
    operation: ItemPlacementOperation,
    moveSelection: PlanogramSelection,
  ) => {
    const result = policy.execute({
      operation,
      pog,
      selection: moveSelection,
    });

    emitMoveResult(operation, result);

    if (result.status !== "committed") return;

    updateSelection(result.selection);
    onCommit?.({
      operation,
      pog: result.pog,
      selection: result.selection,
    });
  };

  const handleProductDragEnd = (
    product: PlanogramSceneProduct,
    event: KonvaEventObject<DragEvent>,
  ) => {
    event.cancelBubble = true;
    const activeDrag = activeProductDragRef.current;

    if (!activeDrag || activeDrag.activeItemId !== product.id || activeDrag.completed) {
      return;
    }

    activeDrag.completed = true;
    activeDrag.restoreVisual();
    activeProductDragRef.current = null;
    setProductDragPreview(null);
    setDropHintIfChanged(null);
    setDragSimulationIfChanged(null);

    const worldPoint = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const targetEntity = hitTestProduct(scene, worldPoint, new Set(activeDrag.itemIds));

    if (!targetEntity) {
      const result: PlanogramMoveResult = {
        pog,
        reason: "missing-target",
        selection: activeDrag.selection,
        status: "rejected",
      };
      emitMoveResult(null, result);
      return;
    }

    const targetProduct = scene.products.find((candidate) => candidate.id === targetEntity.id);
    if (!targetProduct) return;

    const operation = createItemPlacementOperation({
      activeItemId: product.id,
      // Konva dragmove has no evt; reading altKey off it crashed the drag layer.
      intent: activeDrag.intent,
      side: getInsertionSide(targetProduct, worldPoint),
      sourceItemIds: activeDrag.itemIds,
      targetItemId: targetProduct.id,
    });
    submitMoveOperation(operation, activeDrag.selection);
  };

  const handleRelativeMove = (product: PlanogramSceneProduct, direction: RelativeMoveDirection) => {
    const move = getRelativeMove(product, direction);
    if (!move) return;

    updateSelection(move.selection);
    submitMoveOperation(move.operation, move.selection);
  };

  const focusAdjacentProduct = (product: PlanogramSceneProduct, direction: "previous" | "next") => {
    const step = direction === "previous" ? -1 : 1;
    const productIndex = scene.products.findIndex((candidate) => candidate.id === product.id);

    for (
      let index = productIndex + step;
      index >= 0 && index < scene.products.length;
      index += step
    ) {
      const candidate = scene.products[index];
      const button = candidate && productButtonRefs.current.get(candidate.id);
      if (candidate && button && !button.disabled) {
        button.focus();
        return;
      }
    }
  };

  const focusAdjacentEntity = (
    kind: "pegboard" | "placement",
    id: string,
    direction: "previous" | "next",
  ) => {
    const entities = kind === "pegboard" ? scenePegboards : scenePlacements;
    const refs = kind === "pegboard" ? pegboardButtonRefs.current : placementButtonRefs.current;
    const step = direction === "previous" ? -1 : 1;
    const entityIndex = entities.findIndex((candidate) => candidate.id === id);

    for (let index = entityIndex + step; index >= 0 && index < entities.length; index += step) {
      const candidate = entities[index];
      const button = candidate && refs.get(candidate.id);
      if (candidate && button && !button.disabled) {
        button.focus();
        return;
      }
    }
  };

  const handleProductKeyDown = (
    product: PlanogramSceneProduct,
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      activateProduct(product);
      return;
    }

    if (event.key === " " || event.key === "Space") {
      event.preventDefault();
      handleSelection(product, event);
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      const direction = event.key === "ArrowLeft" ? "left" : "right";
      const move = getRelativeMove(product, direction);
      if (move) {
        handleRelativeMove(product, direction);
      } else {
        focusAdjacentProduct(product, direction === "left" ? "previous" : "next");
      }
      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      focusAdjacentProduct(product, event.key === "ArrowUp" ? "previous" : "next");
    }
  };

  const handleSemanticEntityKeyDown = (
    kind: "pegboard" | "placement",
    id: string,
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    const entity =
      kind === "pegboard"
        ? scenePegboards.find((candidate) => candidate.id === id)
        : scenePlacements.find((candidate) => candidate.id === id);
    if (!entity) return;

    if (event.key === "Enter") {
      event.preventDefault();
      if (kind === "pegboard" ? !canSelectPegboard(id) : !canSelectPlacement(id)) return;
      const nextSelection =
        activeSelection.kind === kind && activeSelection.ids.includes(id)
          ? { ...activeSelection, focusedId: id }
          : kind === "pegboard"
            ? selectPegboard(scene, activeSelection, id)
            : selectPlacement(scene, activeSelection, id);
      updateSelection(nextSelection);
      onActivate?.({ id, kind }, nextSelection);
      return;
    }

    if (event.key === " " || event.key === "Space") {
      event.preventDefault();
      const nextSelection =
        kind === "pegboard"
          ? selectPegboard(scene, activeSelection, id, event)
          : selectPlacement(scene, activeSelection, id, event);
      updateSelection(nextSelection);
      return;
    }

    if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowRight" ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();
      focusAdjacentEntity(
        kind,
        id,
        event.key === "ArrowLeft" || event.key === "ArrowUp" ? "previous" : "next",
      );
    }
  };

  const handlePlacementActivation = (
    placement: PlanogramScenePlacement,
    event: KonvaEventObject<MouseEvent | TouchEvent>,
  ) => {
    event.cancelBubble = true;
    activatePlacement(placement);
  };

  const handleProductActivation = (
    product: PlanogramSceneProduct,
    event: KonvaEventObject<MouseEvent | TouchEvent>,
  ) => {
    event.cancelBubble = true;
    activateProduct(product);
  };

  const emitContextRequest = (
    entity: PlanogramEntityRef,
    nextSelection: PlanogramSelection,
    position?: PlanogramContextRequest["position"],
  ) => {
    updateSelection(nextSelection);
    const request: PlanogramContextRequest = position
      ? { entity, position, selection: nextSelection }
      : { entity, selection: nextSelection };
    onContextRequest?.(request);
  };

  const getContextPosition = (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
    const inputEvent = event.evt;
    if ("clientX" in inputEvent && "clientY" in inputEvent) {
      return { clientX: inputEvent.clientX, clientY: inputEvent.clientY };
    }

    const touch = inputEvent.touches[0] ?? inputEvent.changedTouches[0];
    return touch ? { clientX: touch.clientX, clientY: touch.clientY } : undefined;
  };

  const requestContext = (
    event: KonvaEventObject<MouseEvent | TouchEvent>,
    entity: PlanogramEntityRef,
    nextSelection: PlanogramSelection,
  ) => {
    event.cancelBubble = true;
    event.evt.preventDefault();
    emitContextRequest(entity, nextSelection, getContextPosition(event));
  };

  const handleEntityClick = useStableEvent((event: KonvaEventObject<MouseEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleSelection(product, event.evt);
        return;
      }
      case "placement":
        if (canSelectPlacement(entity.id)) {
          updateSelection(selectPlacement(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "pegboard":
        if (canSelectPegboard(entity.id)) {
          updateSelection(selectPegboard(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "container":
        if (canSelectContainer(entity.id)) {
          updateSelection(selectContainer(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "shelf":
        handleShelfSelection(entity.id, event.evt);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityContextMenu = useStableEvent((event: KonvaEventObject<PointerEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;

    if (entity.kind === "product") {
      const product = scene.products.find((candidate) => candidate.id === entity.id);
      if (!product || !canSelectProduct(product)) return;
      const nextSelection =
        activeSelection.kind === "product" && activeSelection.ids.includes(entity.id)
          ? { ...activeSelection, focusedId: entity.id }
          : selectProduct(scene, activeSelection, entity.id);
      requestContext(event, entity, nextSelection);
      return;
    }
    if (entity.kind === "placement") {
      if (canSelectPlacement(entity.id)) {
        requestContext(event, entity, selectPlacement(scene, activeSelection, entity.id));
      }
      return;
    }
    if (entity.kind === "pegboard") {
      if (canSelectPegboard(entity.id)) {
        requestContext(event, entity, selectPegboard(scene, activeSelection, entity.id));
      }
      return;
    }
    if (entity.kind === "container") {
      if (canSelectContainer(entity.id)) {
        const nextSelection =
          activeSelection.kind === "container" && activeSelection.ids.includes(entity.id)
            ? { ...activeSelection, focusedId: entity.id }
            : selectContainer(scene, activeSelection, entity.id);
        requestContext(event, entity, nextSelection);
      }
      return;
    }
    if (entity.kind === "shelf" && canSelectShelf(entity.id)) {
      const nextSelection =
        activeSelection.kind === "shelf" && activeSelection.ids.includes(entity.id)
          ? { ...activeSelection, focusedId: entity.id }
          : selectShelf(scene, activeSelection, entity.id);
      requestContext(event, entity, nextSelection);
    }
  });

  const handleStageContextMenu = useStableEvent((event: KonvaEventObject<PointerEvent>) => {
    const pointer = getStagePointer(event);
    const worldPoint = viewportModel.stageToWorld(activeViewport, pointer);
    const bay = scene.bays.find(
      (candidate) =>
        worldPoint.x >= candidate.x &&
        worldPoint.x <= candidate.x + candidate.width &&
        worldPoint.y >= candidate.y &&
        worldPoint.y <= candidate.y + candidate.height,
    );
    if (!bay) return;

    event.cancelBubble = true;
    event.evt.preventDefault();
    emitContextRequest({ kind: "bay", id: bay.id }, activeSelection, getContextPosition(event));
  });

  const handleEntityDoubleClick = useStableEvent((event: KonvaEventObject<MouseEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleProductActivation(product, event);
        return;
      }
      case "placement": {
        const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
        if (placement) handlePlacementActivation(placement, event);
        return;
      }
      case "pegboard":
        activatePegboard(entity.id);
        return;
      case "container":
        activateContainer(entity.id);
        return;
      case "shelf":
        activateShelf(entity.id);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityDoubleTap = useStableEvent((event: KonvaEventObject<TouchEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleProductActivation(product, event);
        return;
      }
      case "placement": {
        const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
        if (placement) handlePlacementActivation(placement, event);
        return;
      }
      case "pegboard":
        activatePegboard(entity.id);
        return;
      case "container":
        activateContainer(entity.id);
        return;
      case "shelf":
        activateShelf(entity.id);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityTap = useStableEvent((event: KonvaEventObject<TouchEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    event.cancelBubble = true;

    switch (entity.kind) {
      case "product": {
        const product = scene.products.find((candidate) => candidate.id === entity.id);
        if (product) handleSelection(product, event.evt);
        return;
      }
      case "placement":
        if (canSelectPlacement(entity.id)) {
          updateSelection(selectPlacement(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "pegboard":
        if (canSelectPegboard(entity.id)) {
          updateSelection(selectPegboard(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "container":
        if (canSelectContainer(entity.id)) {
          updateSelection(selectContainer(scene, activeSelection, entity.id, event.evt));
        }
        return;
      case "shelf":
        handleShelfSelection(entity.id, event.evt);
        return;
      case "bay":
        return;
    }
  });

  const handleEntityMouseEnter = useStableEvent((event?: KonvaEventObject<MouseEvent>) => {
    const entity = event && getEntityFromEvent(event);
    if (entity) setHoveredEntity(entity);
  });

  const handleEntityMouseLeave = useStableEvent((event?: KonvaEventObject<MouseEvent>) => {
    const entity = event && getEntityFromEvent(event);
    if (entity) clearHoveredEntity(entity.id);
  });

  const handleEntityPointerDown = useStableEvent(
    (event: KonvaEventObject<MouseEvent | TouchEvent>) => {
      backgroundPanMovedRef.current = false;
      event.cancelBubble = true;
    },
  );

  const handleEntityDragStart = useStableEvent((event: KonvaEventObject<DragEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    if (entity.kind === "product") {
      const product = scene.products.find((candidate) => candidate.id === entity.id);
      if (product) handleProductDragStart(product, event);
      return;
    }
    if (entity.kind === "placement") {
      const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
      if (placement) handlePlacementDragStart(placement, event);
      return;
    }
    if (entity.kind === "container") {
      handleContainerDragStart(entity.id, event);
      return;
    }
    if (entity.kind === "shelf") handleShelfDragStart(entity.id, event);
  });

  const handleEntityDragEnd = useStableEvent((event: KonvaEventObject<DragEvent>) => {
    const entity = getEntityFromEvent(event);
    if (!entity) return;
    if (entity.kind === "product") {
      const product = scene.products.find((candidate) => candidate.id === entity.id);
      if (product) handleProductDragEnd(product, event);
      return;
    }
    if (entity.kind === "placement") {
      const placement = scenePlacements.find((candidate) => candidate.id === entity.id);
      if (placement) handlePlacementDragEnd(placement, event);
      return;
    }
    if (entity.kind === "container") {
      const container = scene.containers.find((candidate) => candidate.id === entity.id);
      if (container) handleContainerDragEnd(container, event);
      return;
    }
    if (entity.kind === "shelf") {
      const shelf = scene.shelves.find((candidate) => candidate.id === entity.id);
      const fixture = scene.fixtures.find((candidate) => candidate.id === entity.id);
      if (shelf) handleShelfDragEnd(shelf, event);
      else if (fixture) handleShelfDragEnd(fixture, event);
    }
  });

  const handleEntityDragMove = useStableEvent((event: KonvaEventObject<DragEvent>) => {
    event.cancelBubble = true;

    const activeDrag = activeProductDragRef.current;
    if (!activeDrag || activeDrag.completed) return;
    const entity = getEntityFromEvent(event);
    if (entity?.kind !== "product") return;

    const worldPoint = viewportModel.stageToWorld(activeViewport, getStagePointer(event));
    const dragged = new Set(activeDrag.itemIds);
    const targetEntity = hitTestProduct(scene, worldPoint, dragged);
    const targetProduct = targetEntity
      ? scene.products.find((candidate) => candidate.id === targetEntity.id)
      : undefined;

    if (!targetProduct) {
      setDropHintIfChanged(null);
      setDragSimulationIfChanged(null);
      return;
    }

    const side = getInsertionSide(targetProduct, worldPoint);
    const key = `${targetProduct.id}|${side}|${activeDrag.itemIds.join(",")}`;
    if (dragSimulation?.key === key) return;

    const operation = createItemPlacementOperation({
      activeItemId: activeDrag.activeItemId,
      // Konva dragmove has no evt; reading altKey off it crashed the drag layer.
      intent: activeDrag.intent,
      side,
      sourceItemIds: activeDrag.itemIds,
      targetItemId: targetProduct.id,
    });
    const result = policy.execute({ operation, pog, selection: activeDrag.selection });

    if (result.status !== "committed") {
      setDragSimulationIfChanged(null);
      setDropHintIfChanged(null);
      return;
    }

    const simulated = sceneProjector.project(result.pog, resolvedView.productLabel);
    const landed = simulated.products.filter((candidate) => dragged.has(candidate.id));
    const hint: ProductDropHint | null = landed.length
      ? {
          caretX: Math.min(...landed.map((candidate) => candidate.x)),
          height: Math.max(...landed.map((candidate) => candidate.height)),
          targetId: targetProduct.id,
          width: landed.reduce((sum, candidate) => sum + candidate.width, 0),
          y: Math.min(...landed.map((candidate) => candidate.y)),
        }
      : null;

    setDragSimulationIfChanged({ hint, key, scene: simulated });
    setDropHintIfChanged(hint);
  });

  return (
    <CanvasRoot
      data-product-display-mode={resolvedView.productDisplayMode}
      data-product-label={resolvedView.productLabel}
      data-projection-count={projectionStats.projectionCount}
      data-testid="konva-planogram"
      ref={canvasRootRef}
    >
      <Stage
        height={stageHeight}
        ref={stageRef}
        onClick={handleBackgroundSelection}
        onContextMenu={handleStageContextMenu}
        onMouseDown={startBackgroundPan}
        onMouseLeave={() => {
          cancelInteractions();
          setHoveredEntityState(null);
        }}
        onMouseMove={continueBackgroundPan}
        onMouseUp={stopBackgroundPan}
        onTouchCancel={cancelInteractions}
        onTouchEnd={stopBackgroundPan}
        onTouchMove={continueBackgroundPan}
        onTouchStart={startBackgroundPan}
        onTap={handleBackgroundSelection}
        onWheel={handleWheel}
        scaleX={activeViewport.scale}
        scaleY={activeViewport.scale}
        width={stageWidth}
        x={activeViewport.offsetX}
        y={activeViewport.offsetY}
      >
        <Layer listening={false} name="planogram-structure">
          {scene.bays.map((bay) => (
            <Group key={bay.id} listening={false} name={`bay-${bay.id}`}>
              <Rect
                fill={Color.sand50}
                height={bay.height}
                name={`bay-${bay.id}`}
                stroke={Color.spaceGray}
                width={bay.width}
                x={bay.x}
                y={bay.y}
              />
              {bay.baseHeight > 0 && (
                <>
                  <Rect
                    data-base-height={bay.baseHeight}
                    fill={Color.sand300}
                    fillLinearGradientColorStops={[0, Color.sand300, 1, Color.sand500]}
                    fillLinearGradientEndPoint={{ x: 0, y: bay.baseHeight }}
                    fillLinearGradientStartPoint={{ x: 0, y: 0 }}
                    height={bay.baseHeight}
                    listening={false}
                    name={`bay-base-${bay.id}`}
                    opacity={0.7}
                    width={bay.width}
                    x={bay.x}
                    y={bay.y + bay.height - bay.baseHeight}
                  />
                  <Text
                    align="center"
                    fill={Color.sand700}
                    fontSize={10 / activeViewport.scale}
                    fontStyle="bold"
                    height={bay.baseHeight}
                    listening={false}
                    name={`bay-label-${bay.id}`}
                    text={labels.bay(bay.bayNo)}
                    verticalAlign="middle"
                    width={bay.width}
                    x={bay.x}
                    y={bay.y + bay.height - bay.baseHeight}
                  />
                </>
              )}
              {Array.from({ length: bay.maxNotch }, (_, notchIndex) => {
                const notchBottom = bay.notchOffset + notchIndex * bay.notchSpacing;
                return (
                  <Rect
                    data-notch-index={notchIndex}
                    data-notch-position={notchBottom}
                    fill={Color.sand700}
                    height={1}
                    key={`${bay.id}:notch:${notchIndex}`}
                    listening={false}
                    name={`bay-notch-${bay.id}-${notchIndex}`}
                    width={bay.width}
                    x={bay.x}
                    y={bay.y + bay.height - notchBottom - 1}
                  />
                );
              })}
            </Group>
          ))}
        </Layer>
        {scenePegboards.length > 0 && (
          <Layer name="planogram-pegboards">
            {scenePegboards.map((pegboard) => (
              <Group
                id={pegboard.id}
                key={pegboard.id}
                name={`pegboard-${pegboard.id}`}
                onContextMenu={handleEntityContextMenu}
                onClick={handleEntityClick}
                onDblClick={handleEntityDoubleClick}
                onMouseEnter={handleEntityMouseEnter}
                onMouseLeave={handleEntityMouseLeave}
                onTap={handleEntityTap}
              >
                <Rect
                  fill={Color.gray400}
                  height={pegboard.height}
                  stroke={
                    activeSelection.kind === "pegboard" && activeSelection.ids.includes(pegboard.id)
                      ? Color.primary
                      : Color.spaceGray
                  }
                  strokeWidth={
                    activeSelection.kind === "pegboard" && activeSelection.ids.includes(pegboard.id)
                      ? 2
                      : 1
                  }
                  width={pegboard.width}
                  x={pegboard.x}
                  y={pegboard.y}
                />
                {pegboard.holes.map((hole) => (
                  <Rect
                    fill={Color.textMain}
                    height={hole.height}
                    key={`${pegboard.id}:${hole.column}:${hole.row}`}
                    width={hole.width}
                    x={hole.x}
                    y={hole.y}
                  />
                ))}
              </Group>
            ))}
          </Layer>
        )}
        {scene.containers.length > 0 && (
          <Layer name="planogram-containers">
            {scene.containers.map((container) => {
              const selected =
                activeSelection.kind === "container" && activeSelection.ids.includes(container.id);
              return (
                <Group
                  draggable={canMove}
                  id={container.id}
                  key={container.id}
                  name={`container-${container.id}`}
                  onClick={handleEntityClick}
                  onContextMenu={handleEntityContextMenu}
                  onDblClick={handleEntityDoubleClick}
                  onDragEnd={canMove ? handleEntityDragEnd : undefined}
                  onDragMove={canMove ? handleEntityDragMove : undefined}
                  onDragStart={canMove ? handleEntityDragStart : undefined}
                  onMouseDown={handleEntityPointerDown}
                  onMouseEnter={handleEntityMouseEnter}
                  onMouseLeave={handleEntityMouseLeave}
                  onTap={handleEntityTap}
                  onTouchStart={handleEntityPointerDown}
                >
                  <Rect
                    fill={container.color}
                    height={container.height}
                    stroke={selected ? Color.primary : Color.spaceGray}
                    strokeWidth={selected ? 2 : 1}
                    width={container.width}
                    x={container.x}
                    y={container.y}
                  />
                  <Text
                    align="center"
                    fill={Color.textMain}
                    height={container.height}
                    listening={false}
                    text={container.label}
                    verticalAlign="middle"
                    width={container.width}
                    x={container.x}
                    y={container.y}
                  />
                </Group>
              );
            })}
            {canMove &&
              scene.containers
                .filter(
                  (container) =>
                    activeSelection.kind === "container" &&
                    activeSelection.ids.includes(container.id),
                )
                .flatMap((container) => {
                  const handleSize = 8 / activeViewport.scale;
                  return CONTAINER_RESIZE_SIDES.map((side) => {
                    const position = getContainerResizeHandlePosition(container, side, handleSize);
                    return (
                      <Rect
                        draggable
                        fill={Color.white}
                        height={handleSize}
                        id={container.id}
                        key={`${container.id}:resize:${side}`}
                        name={`container-resize-${side}-${container.id}`}
                        onDragEnd={(event) => handleContainerResizeEnd(container, event)}
                        onDragStart={(event) =>
                          handleContainerResizeStart(container.id, side, event)
                        }
                        onMouseDown={handleEntityPointerDown}
                        stroke={Color.primary}
                        strokeWidth={1}
                        width={handleSize}
                        x={position.x}
                        y={position.y}
                      />
                    );
                  });
                })}
          </Layer>
        )}
        <Layer name="planogram-fixtures">
          {scene.shelves.map((shelf) => (
            <Rect
              draggable={canMove}
              fill={Color.forest950}
              height={shelf.height}
              id={shelf.id}
              key={shelf.id}
              name={`shelf-${shelf.id}`}
              onContextMenu={handleEntityContextMenu}
              onClick={handleEntityClick}
              onDblClick={handleEntityDoubleClick}
              onDragEnd={canMove ? handleEntityDragEnd : undefined}
              onDragStart={canMove ? handleEntityDragStart : undefined}
              onMouseEnter={handleEntityMouseEnter}
              onMouseLeave={handleEntityMouseLeave}
              onTap={handleEntityTap}
              stroke={
                activeSelection.kind === "shelf" && activeSelection.ids.includes(shelf.id)
                  ? Color.primary
                  : Color.spaceGray
              }
              strokeWidth={
                activeSelection.kind === "shelf" && activeSelection.ids.includes(shelf.id) ? 2 : 1
              }
              width={shelf.width}
              x={shelf.x}
              y={shelf.y}
            />
          ))}
          {scene.fixtures.map((fixture) => {
            const selected =
              activeSelection.kind === "shelf" && activeSelection.ids.includes(fixture.id);
            const isHangcell = fixture.fixtureType === "HANGCELL";
            return (
              <Group key={fixture.id}>
                <Rect
                  draggable={canMove}
                  fill={isHangcell ? Color.transparent : Color.gray400}
                  height={fixture.height}
                  id={fixture.id}
                  name={`fixture-${fixture.fixtureType.toLowerCase()}-${fixture.id}`}
                  onContextMenu={handleEntityContextMenu}
                  onClick={handleEntityClick}
                  onDblClick={handleEntityDoubleClick}
                  onDragEnd={canMove ? handleEntityDragEnd : undefined}
                  onDragStart={
                    canMove ? (event) => handleShelfDragStart(fixture.id, event) : undefined
                  }
                  onMouseEnter={handleEntityMouseEnter}
                  onMouseLeave={handleEntityMouseLeave}
                  onTap={handleEntityTap}
                  stroke={
                    selected ? Color.primary : isHangcell ? Color.transparent : Color.spaceGray
                  }
                  strokeWidth={selected ? 2 : 1}
                  width={fixture.width}
                  x={fixture.x}
                  y={fixture.y}
                />
                {isHangcell && (
                  <Rect
                    fill={selected ? Color.pureYellow : Color.forest950}
                    height={fixture.thickness}
                    listening={false}
                    name={`hangcell-bar-${fixture.id}`}
                    stroke={selected ? Color.primary : Color.spaceGray}
                    strokeWidth={selected ? 2 : 1}
                    width={fixture.width}
                    x={fixture.x}
                    y={fixture.y - fixture.thickness}
                  />
                )}
              </Group>
            );
          })}
        </Layer>
        {scenePlacements.length > 0 && (
          <Layer name="planogram-placements">
            {scenePlacements.map((placement) => {
              const selected =
                activeSelection.kind === "placement" && activeSelection.ids.includes(placement.id);
              const asset = placementAssets.get(placement.id);
              return (
                <Group
                  draggable={canMove}
                  id={placement.id}
                  key={placement.id}
                  name={`placement-${placement.id}`}
                  onContextMenu={handleEntityContextMenu}
                  onClick={handleEntityClick}
                  onDblClick={handleEntityDoubleClick}
                  onDblTap={handleEntityDoubleTap}
                  onDragEnd={canMove ? handleEntityDragEnd : undefined}
                  onDragStart={canMove ? handleEntityDragStart : undefined}
                  onMouseDown={handleEntityPointerDown}
                  onMouseEnter={handleEntityMouseEnter}
                  onMouseLeave={handleEntityMouseLeave}
                  onTap={handleEntityTap}
                  onTouchStart={handleEntityPointerDown}
                  x={placement.x}
                  y={placement.y}
                >
                  <PlacementCells
                    asset={asset}
                    color={resolvedView.productColors?.get(placement.id)}
                    displayMode={resolvedView.productDisplayMode}
                    labelFontSize={globalLabelFontSize}
                    placement={placement}
                    scale={activeViewport.scale}
                    showLabel={showProductLabels}
                  />
                  {selected && (
                    <>
                      <Rect
                        height={placement.height}
                        listening={false}
                        stroke={Color.white}
                        strokeWidth={Math.max(6, (worldHeight / 600) * 4)}
                        width={placement.width}
                        x={0}
                        y={0}
                      />
                      <Rect
                        height={placement.height}
                        listening={false}
                        stroke={Color.primary}
                        strokeWidth={Math.max(3, (worldHeight / 600) * 2)}
                        width={placement.width}
                        x={0}
                        y={0}
                      />
                    </>
                  )}
                </Group>
              );
            })}
          </Layer>
        )}
        <Layer name="planogram-products">
          {renderedProducts.map((product) => {
            const isActivePreview = productDragPreview?.activeItemId === product.id;
            const isPreviewMember = productDragPreview?.itemIds.includes(product.id) ?? false;

            return (
              <Group
                dragDistance={GESTURE_THRESHOLD}
                draggable={canMove}
                id={product.id}
                key={product.id}
                name={`product-${product.id}`}
                onContextMenu={handleEntityContextMenu}
                onClick={handleEntityClick}
                onDblClick={handleEntityDoubleClick}
                onDblTap={handleEntityDoubleTap}
                onMouseDown={handleEntityPointerDown}
                onDragEnd={canMove ? handleEntityDragEnd : undefined}
                onDragMove={canMove ? handleEntityDragMove : undefined}
                onDragStart={canMove ? handleEntityDragStart : undefined}
                onMouseEnter={handleEntityMouseEnter}
                onMouseLeave={handleEntityMouseLeave}
                onTap={handleEntityTap}
                onTouchStart={handleEntityPointerDown}
                visible={!isPreviewMember || isActivePreview}
                x={product.x}
                y={product.y}
              >
                <ProductCells
                  asset={assets.get(getAssetKey(product))}
                  color={resolvedView.productColors?.get(product.id)}
                  detail={isProductVisible(product) ? "full" : "reduced"}
                  displayMode={resolvedView.productDisplayMode}
                  labelFontSize={globalLabelFontSize}
                  product={product}
                  scale={activeViewport.scale}
                  showLabel={showProductLabels}
                />
                {selectedProductIds.has(product.id) && (
                  <>
                    <Rect
                      height={product.height}
                      listening={false}
                      stroke={Color.white}
                      strokeWidth={Math.max(6, (worldHeight / 600) * 4)}
                      width={product.width}
                      x={0}
                      y={0}
                    />
                    <Rect
                      height={product.height}
                      listening={false}
                      stroke={Color.primary}
                      strokeWidth={Math.max(3, (worldHeight / 600) * 2)}
                      width={product.width}
                      x={0}
                      y={0}
                    />
                  </>
                )}
                {isActivePreview && (
                  <Group listening={false} name="product-drag-preview">
                    {scene.products
                      .filter(
                        (candidate) =>
                          candidate.id !== product.id &&
                          productDragPreview.itemIds.includes(candidate.id),
                      )
                      .map((candidate) => (
                        <Group
                          key={candidate.id}
                          name={`drag-preview-product-${candidate.id}`}
                          x={candidate.x - product.x}
                          y={candidate.y - product.y}
                        >
                          <ProductCells
                            asset={assets.get(getAssetKey(candidate))}
                            color={resolvedView.productColors?.get(candidate.id)}
                            detail={isProductVisible(candidate) ? "full" : "reduced"}
                            displayMode={resolvedView.productDisplayMode}
                            product={candidate}
                            scale={activeViewport.scale}
                            showLabel={showProductLabels}
                          />
                          <Rect
                            height={candidate.height}
                            listening={false}
                            stroke={Color.primary}
                            strokeWidth={2}
                            width={candidate.width}
                          />
                        </Group>
                      ))}
                  </Group>
                )}
              </Group>
            );
          })}
        </Layer>
        {dropPreview && (
          <Layer listening={false} name="planogram-drop-preview">
            <Rect
              dash={[6, 4]}
              fill={Color.primary}
              height={dropPreview.height}
              listening={false}
              name="drop-preview-ghost"
              opacity={0.35}
              stroke={Color.primary}
              strokeWidth={2}
              width={dropPreview.width}
              x={dropPreview.x}
              y={dropPreview.y}
            />
          </Layer>
        )}
        {dropHint && (
          <Layer listening={false} name="planogram-drop-hint">
            <Rect
              dash={[6, 4]}
              fill={Color.primary}
              height={dropHint.height}
              listening={false}
              name="drop-hint-ghost"
              opacity={0.22}
              stroke={Color.primary}
              strokeWidth={1 / activeViewport.scale}
              width={dropHint.width}
              x={dropHint.caretX}
              y={dropHint.y}
            />
            <Rect
              fill={Color.primary}
              height={dropHint.height}
              listening={false}
              name="drop-hint-caret"
              width={3 / activeViewport.scale}
              x={dropHint.caretX - 1.5 / activeViewport.scale}
              y={dropHint.y}
            />
          </Layer>
        )}
        {[...scene.shelves, ...scene.fixtures].some(
          ({ merchandisableWidth, width }) => merchandisableWidth < width,
        ) && (
          <Layer listening={false} name="planogram-non-merchandisable-areas">
            {[...scene.shelves, ...scene.fixtures]
              .filter(({ merchandisableWidth, width }) => merchandisableWidth < width)
              .map((shelf) => {
                // HANGCELL y/height is the hang zone; the thickness band sits above that y.
                const isHangcell = "fixtureType" in shelf && shelf.fixtureType === "HANGCELL";
                const barY = isHangcell ? shelf.y - shelf.thickness : shelf.y;

                return (
                  <Fragment key={shelf.id}>
                    <Rect
                      dash={[8, 5]}
                      fill={Color.alertMediumBg}
                      height={shelf.thickness}
                      name={`non-merchandisable-area-${shelf.id}`}
                      opacity={0.85}
                      stroke={Color.alertMediumIcon}
                      strokeWidth={1}
                      width={shelf.width - shelf.merchandisableWidth}
                      x={shelf.x + shelf.merchandisableWidth}
                      y={barY}
                    />
                    {shelf.nonMerchandisableExtensionHeight > 0 && (
                      <Rect
                        dash={[8, 5]}
                        fill={Color.alertMediumBg}
                        height={shelf.nonMerchandisableExtensionHeight}
                        name={`non-merchandisable-area-extension-${shelf.id}`}
                        opacity={0.3}
                        stroke={Color.alertMediumIcon}
                        strokeWidth={1}
                        width={shelf.width - shelf.merchandisableWidth}
                        x={shelf.x + shelf.merchandisableWidth}
                        y={shelf.nonMerchandisableExtensionY}
                      />
                    )}
                  </Fragment>
                );
              })}
          </Layer>
        )}
        <PresentationOverlays
          entities={overlayEntities}
          onBadgeActivate={onOverlayBadgeActivate}
          presentations={overlayPresentations}
          scale={activeViewport.scale}
        />
      </Stage>
      <KonvaPlanogramViewportChrome
        labels={labels}
        onReset={resetViewport}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
      />
      <AccessibleControls aria-label={labels.containers} role="list">
        {scene.containers.map((container) => {
          const entity = getContainerEntity(container);
          const selected =
            activeSelection.kind === "container" && activeSelection.ids.includes(container.id);
          return (
            <AccessibleProductGroup key={container.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectContainer(container.label)}
                aria-pressed={selected}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="container"
                disabled={!canSelectContainer(container.id)}
                onClick={(event) => {
                  if (canSelectContainer(container.id)) {
                    updateSelection(selectContainer(scene, activeSelection, container.id, event));
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectContainer(container.id)) {
                    emitContextRequest(
                      entity,
                      selectContainer(scene, activeSelection, container.id),
                    );
                  }
                }}
                onDoubleClick={() => {
                  activateContainer(container.id);
                }}
                onFocus={() =>
                  updateSelection(focusEntity(scene, activeSelection, "container", container.id))
                }
                data-planogram-focused={activeSelection.focusedId === container.id}
                tabIndex={0}
                type="button"
              >
                {container.label}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.products} role="list">
        {scene.products.map((product) => {
          const entity = getProductEntity(product);
          const label = product.label || product.name || product.productCode || product.id;
          const productStatusLabel = product.isNewInsertedItem ? labels.newItemIndicator : null;
          const focused = activeSelection.focusedId === product.id;
          const leftMove = getRelativeMove(product, "left");
          const rightMove = getRelativeMove(product, "right");
          return (
            <AccessibleProductGroup key={product.id} role="listitem">
              <AccessibleProductButton
                aria-label={combineAccessibleLabels(
                  labels.selectProduct(label),
                  productStatusLabel,
                )}
                aria-pressed={selectedProductIds.has(product.id)}
                aria-keyshortcuts="Enter Space ArrowLeft ArrowRight ArrowUp ArrowDown"
                data-planogram-entity-id={entity.id}
                data-planogram-status={product.isNewInsertedItem ? "new-item" : undefined}
                disabled={!canSelectProduct(product)}
                data-planogram-focused={focused}
                onClick={(event) => handleSelection(product, event)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  emitContextRequest(entity, selectProduct(scene, activeSelection, product.id));
                }}
                onFocus={() => handleFocus(product)}
                ref={(button) => {
                  if (button) productButtonRefs.current.set(product.id, button);
                  else productButtonRefs.current.delete(product.id);
                }}
                onDoubleClick={() => {
                  activateProduct(product);
                }}
                onKeyDown={(event) => handleProductKeyDown(product, event)}
                tabIndex={0}
                type="button"
              >
                {label}
              </AccessibleProductButton>
              <AccessibleMoveButton
                aria-label={labels.moveProductLeft(label)}
                data-planogram-entity-id={entity.id}
                data-planogram-operation="move-left"
                disabled={!leftMove}
                onClick={() => {
                  if (!leftMove) return;
                  updateSelection(leftMove.selection);
                  submitMoveOperation(leftMove.operation, leftMove.selection);
                }}
                type="button"
              >
                {labels.moveLeft}
              </AccessibleMoveButton>
              <AccessibleMoveButton
                aria-label={labels.moveProductRight(label)}
                data-planogram-entity-id={entity.id}
                data-planogram-operation="move-right"
                disabled={!rightMove}
                onClick={() => {
                  if (!rightMove) return;
                  updateSelection(rightMove.selection);
                  submitMoveOperation(rightMove.operation, rightMove.selection);
                }}
                type="button"
              >
                {labels.moveRight}
              </AccessibleMoveButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.pegboards} role="list">
        {scenePegboards.map((pegboard) => {
          const entity = getPegboardEntity(pegboard);
          const selected =
            activeSelection.kind === "pegboard" && activeSelection.ids.includes(pegboard.id);
          return (
            <AccessibleProductGroup key={pegboard.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectPegboard(pegboard.id)}
                aria-pressed={selected}
                aria-keyshortcuts="Enter Space ArrowLeft ArrowRight ArrowUp ArrowDown"
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="pegboard"
                disabled={!canSelectPegboard(pegboard.id)}
                onClick={(event) => {
                  if (canSelectPegboard(pegboard.id)) {
                    updateSelection(selectPegboard(scene, activeSelection, pegboard.id, event));
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectPegboard(pegboard.id)) {
                    emitContextRequest(entity, selectPegboard(scene, activeSelection, pegboard.id));
                  }
                }}
                onDoubleClick={() => {
                  activatePegboard(pegboard.id);
                }}
                onFocus={() =>
                  updateSelection(focusEntity(scene, activeSelection, "pegboard", pegboard.id))
                }
                onKeyDown={(event) => handleSemanticEntityKeyDown("pegboard", pegboard.id, event)}
                ref={(button) => {
                  if (button) pegboardButtonRefs.current.set(pegboard.id, button);
                  else pegboardButtonRefs.current.delete(pegboard.id);
                }}
                data-planogram-focused={activeSelection.focusedId === pegboard.id}
                tabIndex={0}
                type="button"
              >
                {pegboard.id}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.placements} role="list">
        {scenePlacements.map((placement) => {
          const entity = getPlacementEntity(placement);
          const selected =
            activeSelection.kind === "placement" && activeSelection.ids.includes(placement.id);
          const placementStatusLabel =
            placement.facingChangeColor && placement.facingChangeType
              ? labels.pegboardFacingChangeIndicator(placement.facingChangeType)
              : null;
          return (
            <AccessibleProductGroup key={placement.id} role="listitem">
              <AccessibleProductButton
                aria-label={combineAccessibleLabels(
                  labels.selectPlacement(placement.label),
                  placementStatusLabel,
                )}
                aria-pressed={selected}
                aria-keyshortcuts="Enter Space ArrowLeft ArrowRight ArrowUp ArrowDown"
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="placement"
                data-planogram-facing-change={placement.facingChangeType ?? undefined}
                disabled={!canSelectPlacement(placement.id)}
                onClick={(event) => {
                  if (canSelectPlacement(placement.id)) {
                    updateSelection(selectPlacement(scene, activeSelection, placement.id, event));
                  }
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectPlacement(placement.id)) {
                    emitContextRequest(
                      entity,
                      selectPlacement(scene, activeSelection, placement.id),
                    );
                  }
                }}
                onDoubleClick={() => {
                  activatePlacement(placement);
                }}
                onFocus={() =>
                  updateSelection(focusEntity(scene, activeSelection, "placement", placement.id))
                }
                onKeyDown={(event) => handleSemanticEntityKeyDown("placement", placement.id, event)}
                ref={(button) => {
                  if (button) placementButtonRefs.current.set(placement.id, button);
                  else placementButtonRefs.current.delete(placement.id);
                }}
                data-planogram-focused={activeSelection.focusedId === placement.id}
                tabIndex={0}
                type="button"
              >
                {placement.label}
              </AccessibleProductButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.shelves} role="list">
        {scene.shelves.map((shelf) => {
          const entity: PlanogramEntityRef = { id: shelf.id, kind: "shelf" };
          const selected =
            activeSelection.kind === "shelf" && activeSelection.ids.includes(shelf.id);
          const shelfSelection = () =>
            activeSelection.kind === "shelf" && activeSelection.ids.includes(shelf.id)
              ? activeSelection
              : selectShelf(scene, activeSelection, shelf.id);
          const resizeShelf = (width: number) => {
            const nextSelection = shelfSelection();
            updateSelection(nextSelection);
            submitShelfOperation(
              createShelfResizeOperation({
                activeShelfId: shelf.id,
                dimensions: { width },
                shelfIds: nextSelection.ids,
              }),
              nextSelection,
              () => undefined,
            );
          };
          return (
            <AccessibleProductGroup key={shelf.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectShelf(shelf.shelfNo)}
                aria-pressed={selected}
                data-planogram-entity-id={shelf.id}
                data-planogram-entity-kind="shelf"
                disabled={!canSelectShelf(shelf.id)}
                onClick={(event) => handleShelfSelection(shelf.id, event)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectShelf(shelf.id)) {
                    emitContextRequest(entity, selectShelf(scene, activeSelection, shelf.id));
                  }
                }}
                onDoubleClick={() => {
                  activateShelf(shelf.id);
                }}
                type="button"
              >
                {labels.shelf(shelf.shelfNo)}
              </AccessibleProductButton>
              <AccessibleResizeButton
                aria-label={labels.resizeShelfWider(shelf.shelfNo)}
                data-planogram-entity-id={shelf.id}
                data-planogram-operation="shelf-resize"
                disabled={!canMove || !canSelectShelf(shelf.id)}
                onClick={() => resizeShelf(shelf.width + 1)}
                type="button"
              >
                {labels.resizeShelfWiderText}
              </AccessibleResizeButton>
              <AccessibleResizeButton
                aria-label={labels.resizeShelfNarrower(shelf.shelfNo)}
                data-planogram-entity-id={shelf.id}
                data-planogram-operation="shelf-resize"
                disabled={!canMove || !canSelectShelf(shelf.id) || shelf.width <= 1}
                onClick={() => resizeShelf(Math.max(1, shelf.width - 1))}
                type="button"
              >
                {labels.resizeShelfNarrowerText}
              </AccessibleResizeButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
      <AccessibleControls aria-label={labels.fixtures} role="list">
        {scene.fixtures.map((fixture) => {
          const entity: PlanogramEntityRef = { id: fixture.id, kind: "shelf" };
          const selected =
            activeSelection.kind === "shelf" && activeSelection.ids.includes(fixture.id);
          const fixtureSelection = () =>
            activeSelection.kind === "shelf" && activeSelection.ids.includes(fixture.id)
              ? activeSelection
              : selectShelf(scene, activeSelection, fixture.id);
          const resizeFixture = (width: number) => {
            const nextSelection = fixtureSelection();
            updateSelection(nextSelection);
            submitShelfOperation(
              createShelfResizeOperation({
                activeShelfId: fixture.id,
                dimensions: { width },
                shelfIds: nextSelection.ids,
              }),
              nextSelection,
              () => undefined,
            );
          };
          return (
            <AccessibleProductGroup key={fixture.id} role="listitem">
              <AccessibleProductButton
                aria-label={labels.selectFixture(fixture.fixtureType, fixture.shelfNo)}
                aria-pressed={selected}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="fixture"
                disabled={!canSelectShelf(fixture.id)}
                onClick={(event) => handleShelfSelection(fixture.id, event)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (canSelectShelf(fixture.id)) {
                    emitContextRequest(entity, selectShelf(scene, activeSelection, fixture.id));
                  }
                }}
                onDoubleClick={() => {
                  activateShelf(fixture.id);
                }}
                type="button"
              >
                {labels.fixture(fixture.fixtureType, fixture.shelfNo)}
              </AccessibleProductButton>
              <AccessibleResizeButton
                aria-label={labels.resizeFixtureNarrower(fixture.fixtureType, fixture.shelfNo)}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="fixture"
                data-planogram-operation="fixture-resize"
                disabled={!canMove || !canSelectShelf(fixture.id) || fixture.width <= 1}
                onClick={() => resizeFixture(Math.max(1, fixture.width - 1))}
                type="button"
              >
                {labels.resizeFixtureNarrowerText}
              </AccessibleResizeButton>
              <AccessibleResizeButton
                aria-label={labels.resizeFixtureWider(fixture.fixtureType, fixture.shelfNo)}
                data-planogram-entity-id={entity.id}
                data-planogram-entity-kind="fixture"
                data-planogram-operation="fixture-resize"
                disabled={!canMove || !canSelectShelf(fixture.id)}
                onClick={() => resizeFixture(fixture.width + 1)}
                type="button"
              >
                {labels.resizeFixtureWiderText}
              </AccessibleResizeButton>
            </AccessibleProductGroup>
          );
        })}
      </AccessibleControls>
    </CanvasRoot>
  );
};
